import type { GeneratedFile, UserAttachment } from '@Models';

// Same limits as the core API, so a bad file shows its reason before Send.
const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;
// The core API strips these from file names before it stores them.
// eslint-disable-next-line no-control-regex
const UNSAFE_NAME_CHARS = /[\x00-\x1f\x7f"]/g;

const MIME_TYPES = new Map<string, string>([
  ['csv', 'text/csv'],
  ['xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  ['pptx', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
  ['pdf', 'application/pdf'],
]);
const MACRO_TYPES = new Set(['xlsm', 'docm', 'pptm']);
const IMAGE_TYPES = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'svg', 'bmp', 'tif', 'tiff']);

export const ATTACHMENT_ACCEPT = Array.from(MIME_TYPES.keys(), (type) => `.${type}`).join(',');
export const ATTACHMENT_HINT = `${Array.from(MIME_TYPES.keys(), (type) => type.toUpperCase()).join(' · ')} · ≤ ${
  MAX_ATTACHMENT_BYTES / 1024 / 1024
} MB`;

type AttachmentErrorCode =
  | 'attachments_disabled'
  | 'too_many_files'
  | 'unsupported_type'
  | 'macro_file'
  | 'too_large'
  | 'duplicate_name'
  | 'invalid_file'
  | 'images_not_supported';

const ERROR_KEYS: Record<AttachmentErrorCode, string> = {
  attachments_disabled: 'Agent.AttachmentErrorDisabled',
  too_many_files: 'Agent.AttachmentErrorTooMany',
  unsupported_type: 'Agent.AttachmentErrorUnsupportedType',
  macro_file: 'Agent.AttachmentErrorMacroFile',
  too_large: 'Agent.AttachmentErrorTooLarge',
  duplicate_name: 'Agent.AttachmentErrorDuplicateName',
  invalid_file: 'Agent.AttachmentErrorInvalidFile',
  images_not_supported: 'Agent.AttachmentErrorImagesNotSupported',
};

/** A file the core API refused, with the code and file name from its 400/403 body. */
export class AttachmentUploadError extends Error {
  readonly code: string;

  readonly fileName: string | null;

  constructor(code: string, fileName: string | null) {
    super(`Attachment rejected: ${code}`);
    this.name = 'AttachmentUploadError';
    this.code = code;
    this.fileName = fileName;
    // Keeps `instanceof` working when TypeScript compiles classes to ES5.
    Object.setPrototypeOf(this, AttachmentUploadError.prototype);
  }
}

/** Files sent with a message, and how the sender learns whether the core API took them. */
export interface MessageUpload {
  files: File[];
  onAccepted: () => void;
  /**
   * The files are on no message: refused (`error`), cancelled or failed.
   * `isOpen` is false when the user left the conversation before that.
   */
  onRejected: (error: AttachmentUploadError | undefined, isOpen: boolean) => void;
}

/** The name the core API stores, so local records and refused names match it. */
export const storedFileName = (fileName: string): string => fileName.replace(UNSAFE_NAME_CHARS, '').trim();

const extensionOf = (fileName: string): string => {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
};

/** Why `file` cannot be sent next to the `kept` files, or undefined when it can. */
export const checkAttachment = (file: File, kept: File[]): AttachmentErrorCode | undefined => {
  const type = extensionOf(file.name);
  if (file.type.startsWith('image/') || IMAGE_TYPES.has(type)) return 'images_not_supported';
  if (MACRO_TYPES.has(type)) return 'macro_file';
  if (!MIME_TYPES.has(type)) return 'unsupported_type';
  if (file.size === 0) return 'invalid_file';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'too_large';
  if (kept.some((other) => storedFileName(other.name) === storedFileName(file.name))) return 'duplicate_name';
  if (kept.length >= MAX_ATTACHMENTS) return 'too_many_files';
  return undefined;
};

export const attachmentErrorKey = (code?: string): string =>
  code && Object.prototype.hasOwnProperty.call(ERROR_KEYS, code)
    ? ERROR_KEYS[code as AttachmentErrorCode]
    : 'Agent.AttachmentErrorGeneric';

export const mimeTypeForName = (fileName: string): string => MIME_TYPES.get(extensionOf(fileName)) ?? '';

/** Same title the core API gives a conversation whose first message has files and no text. */
export const filesTitle = (files: File[]): string => {
  const names = files.map((file) => storedFileName(file.name));
  return names.length <= 1 ? names.join('') : `${names[0]} + ${names.length - 1}`;
};

/** The record the core API keeps for a sent file, built locally for the optimistic message. */
export const toAttachmentRecord = (file: File, interactionTimestamp: string): UserAttachment => ({
  file_name: storedFileName(file.name),
  file_type: extensionOf(file.name),
  size_bytes: file.size,
  interaction_timestamp: interactionTimestamp,
});

/** Sent attachments render with the same file card as the agent's files. */
export const toCardFile = ({
  file_name: fileName,
  file_type: type,
  size_bytes: size,
}: UserAttachment): GeneratedFile => ({
  file_name: fileName,
  file_size: size,
  file_type: MIME_TYPES.get(type) ?? type,
});
